import { useMemo } from 'react';

/**
 * 정산 변경 도메인 (기준일 / 완료일) 의 배치 상세 모달에서 scope 선택용 옵션을
 * derive 할 때 필요한 최소 contract — `BatchDetailItem` 의 subset 만 만족하면 됨.
 */
type BatchItemForScope = {
  storeId: number;
  storeName?: string;
  orderItemId?: string;
};

export type BatchScopeOptions = {
  /** 배치 내 고유 storeId + 라벨용 storeName 매핑. */
  storeOptionList: Array<{ storeId: number; storeName?: string }>;
  /** 배치 내 고유 orderItemId 리스트. orderItemId 가 없는 행(예: extra/return) 은 제외. */
  orderItemIds: string[];
};

/**
 * BatchDetailModal 의 scope 드롭다운 옵션을 derive 하는 훅.
 *
 * 사용자가 직접 타이핑하지 않고 "이 배치에 이 브랜드만 원복" 같은 식으로 고르게 한다.
 * storeId 만으로는 어느 브랜드인지 식별하기 어려워 storeName 도 함께 묶어 라벨 표기에 사용.
 *
 * `items` 가 undefined 인 경우(detailQuery 로딩 중) 빈 옵션 리스트로 안전하게 처리.
 *
 * @example
 * const { storeOptionList, orderItemIds } = useBatchScopeOptions(detailQuery.data?.items);
 */
export const useBatchScopeOptions = (
  items: readonly BatchItemForScope[] | undefined,
): BatchScopeOptions =>
  useMemo(() => {
    const list = items ?? [];
    const storeMap = new Map(
      list.map((it) => [it.storeId, it.storeName] as const),
    );
    const storeOptionList = Array.from(storeMap, ([storeId, storeName]) => ({
      storeId,
      storeName,
    }));
    const orderItemIds = Array.from(
      new Set(
        list.map((it) => it.orderItemId).filter((x): x is string => !!x),
      ),
    );
    return { storeOptionList, orderItemIds };
  }, [items]);

/**
 * 롤백 scope. 두 도메인이 동일하게 사용 — 백엔드 `RollbackScope` 와 일치.
 */
export type RollbackScope = 'all' | 'byIds' | 'byStoreId' | 'byOrderItemIds';

type ValidateRollbackInputsArgs = {
  scope: RollbackScope;
  reason: string;
  selectedIds: number[];
  storeId: number | null;
  orderItemIds: string[];
};

/**
 * 롤백 요청 전 클라이언트 측 사전 검증.
 *
 * 백엔드 DTO 의 ValidateIf 규칙과 동일한 조건 — scope 별 필요 필드가 갖춰져
 * 있는지 확인. 실패 시 사용자에게 보여줄 한국어 에러 메시지 반환, 통과면 null.
 *
 * 두 정산 변경 도메인 (기준일 / 완료일) 이 동일한 4-scope 정책이므로 본 함수를 공유.
 */
export const validateRollbackInputs = (
  args: ValidateRollbackInputsArgs,
): string | null => {
  if (!args.reason.trim()) return '롤백 사유를 입력해주세요.';
  switch (args.scope) {
    case 'byIds':
      if (args.selectedIds.length === 0)
        return '위 체크박스에서 롤백할 항목을 선택해주세요.';
      break;
    case 'byStoreId':
      if (args.storeId == null) return '롤백할 브랜드 ID를 선택해주세요.';
      break;
    case 'byOrderItemIds':
      if (args.orderItemIds.length === 0)
        return '롤백할 상품 주문번호를 선택해주세요.';
      break;
    case 'all':
    default:
      break;
  }
  return null;
};
